import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  HStack,
  IconButton,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

import AIAgentConversationHistory from './AIAgentConversationHistory';
import AIAgentError from './AIAgentError';
import AIAgentHeader from './AIAgentHeader';
import AIAgentInputForm from './AIAgentInputForm';
import AIAgentNudgePopover from './components/overlay/AIAgentNudgePopover';
import AIAgentPreviewHeader from './AIAgentPreviewHeader';
import AIAgentWelcome from './AIAgentWelcome';
import { Close } from '@/client/components/icons/ContinuIcons';
import EddyAnimation from './EddyAnimation';
import { motion } from 'framer-motion';
import { useAIAgentStore } from './AIAgentStore';
import { useListUserConversations } from './hooks/useListUserConversations';
import { useTranslation } from 'react-i18next';

export default function AIAgentv2() {
  const { t } = useTranslation();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const btnRef = useRef(null);
  const {
    showWelcome,
    setShowWelcome,
    activeTab,
    eddyDrawerIsOpen,
    setEddyDrawerIsOpen,
    previewExpert,
    setPreviewExpert,
    setCurrentConversationId,
  } = useAIAgentStore();
  const [isCollapsed, setIsCollapsed] = useState(isMobile);
  const [renderHideButton, setRenderHideButton] = useState(false);

  const { data: conversations, isError: conversationsError } = useListUserConversations();

  const hasConversationHistory = conversations?.edges && conversations?.edges?.length > 0;

  useEffect(() => (isMobile ? setIsCollapsed(true) : setIsCollapsed(false)), [isMobile]);

  useEffect(() => {
    if (hasConversationHistory) {
      setShowWelcome(false);
    }
  }, [hasConversationHistory]);

  return (
    <>
      <AIAgentNudgePopover />

      <motion.button
        ref={btnRef}
        onClick={() => {
          setEddyDrawerIsOpen(true);

          if (!isMobile) {
            setIsCollapsed(false);
          }
        }}
        initial={{
          right: '30px',
          scale: 1,
        }}
        animate={{
          right: isCollapsed ? '-80px' : '30px',
          scale: isCollapsed ? 0.9 : 1,
        }}
        transition={{
          type: 'spring',
          stiffness: 300,
          damping: 25,
          duration: 0.3,
        }}
        style={{
          position: 'fixed',
          bottom: isMobile ? '2%' : '75px',
          zIndex: 998,
          paddingRight: '16px',
          paddingLeft: '8px',
          paddingTop: '8px',
          paddingBottom: '8px',
          borderRadius: '16px',
          backgroundColor: 'white',
          fontSize: '14px',
          fontWeight: 500,
          color: 'var(--chakra-colors-neutral-1400)',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0px',
          boxShadow: '1px 12px 47px 0px rgba(0, 0, 0, 0.45)',
        }}
        whileTap={{ scale: 0.96 }}
        whileHover={{
          y: -5,
          transition: {
            type: 'spring',
            stiffness: 600,
            damping: 15,
            mass: 1,
            restDelta: 0.005,
            restSpeed: 0.01,
          },
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'white';

          if (!isCollapsed) {
            setRenderHideButton(true);
          }
        }}
        onMouseLeave={(e) => {
          setRenderHideButton(false);
          e.currentTarget.style.backgroundColor = 'white';
        }}
      >
        <HStack spacing={1}>
          <EddyAnimation size={40} />

          <Text>{t('aiAgent.ask')}</Text>
        </HStack>

        {renderHideButton && (
          <IconButton
            position="absolute"
            left={-1}
            top={-1}
            height={18}
            minWidth={18}
            borderRadius="full"
            aria-label="Close"
            icon={<Close boxSize={2.5} />}
            backgroundColor="neutral.50"
            color="neutral.800"
            _hover={{ backgroundColor: 'neutral.200' }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              setRenderHideButton(false);
              setIsCollapsed(true);
            }}
          />
        )}
      </motion.button>

      <Drawer
        isOpen={eddyDrawerIsOpen}
        placement="right"
        size="sm"
        onClose={() => {
          if (previewExpert) {
            setCurrentConversationId('');
            setPreviewExpert(undefined);
          }

          setEddyDrawerIsOpen(false);
        }}
        finalFocusRef={btnRef}
      >
        <DrawerContent marginTop="80px" paddingTop={6}>
          <DrawerCloseButton color="neutral.1300" />

          <DrawerBody
            paddingX={6}
            paddingBottom={6}
            display="flex"
            flexDirection="column"
            overflowY="hidden"
          >
            {conversationsError ? (
              <AIAgentError />
            ) : (
              <>
                {showWelcome && <AIAgentWelcome />}

                {!showWelcome && (
                  <>
                    {hasConversationHistory && !previewExpert && <AIAgentHeader />}

                    {previewExpert && <AIAgentPreviewHeader />}

                    {activeTab === 'history' && <AIAgentConversationHistory />}

                    {activeTab === 'conversation' && <AIAgentInputForm />}
                  </>
                )}
              </>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}
